import { useState } from 'react';
import {
  Autocomplete,
  AutocompleteChangeReason,
  ListItem,
  TextField,
} from '@mui/material';
import './FormSearchDropdown.scss';
import { useTranslation } from 'react-i18next';

export type FormSearchDropdownProps<T> = {
  options: T[];
  selectedOption: T | null;
  label: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | string[];
  onSelect: (option: T | null, reason: AutocompleteChangeReason) => void;
};

export const FormSearchDropdown = <T extends { name: string }>({
  options,
  selectedOption,
  className = '',
  label = '',
  disabled,
  required,
  error,
  onSelect,
}: FormSearchDropdownProps<T>) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="d-flex flex-column">
      <label className="form-label">
        {label}
        {required && <span className="mx-1">*</span>}
      </label>
      <Autocomplete
        id="form-search-autocomplete"
        className={`form-search-dropdown flex-grow-1 ${className}`}
        value={selectedOption}
        options={options}
        color="primary"
        inputValue={inputValue}
        disabled={disabled}
        size="small"
        openOnFocus
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderOption={(params, option) => (
          <ListItem key={option.name} color="primary" {...params}>
            {option.name}
          </ListItem>
        )}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <div className="d-flex flex-column">
            <TextField
              {...params}
              sx={{ background: '#fff' }}
              color="primary"
              variant="outlined"
              size="small"
              placeholder={t('input_search_placeholder')}
            />
            {error && (
              <span className="error-text">
                {typeof error === 'object' ? error.join(', ') : error}
              </span>
            )}
          </div>
        )}
        onChange={(
          _: any,
          newValue: T | null,
          reason: AutocompleteChangeReason,
        ) => {
          onSelect(newValue, reason);
        }}
      />
    </div>
  );
};
